import { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PeopleIcon from '@mui/icons-material/People';
import { useQueryClient } from '@tanstack/react-query';
import {
  useListTeams,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useGetTeam,
  useAddTeamMember,
  useRemoveTeamMember,
  useAssignTeamManager,
  useUnassignTeamManager,
  useListMembers,
  getListTeamsQueryKey,
  getGetTeamQueryKey,
} from '../../api/generated/generated';
import type { Member, Team, TeamMemberRef } from '../../api/generated';
import { getErrorMessage } from '../../api/client';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useSnackbar } from '../common/SnackbarProvider';

interface TeamsManagerProps {
  orgId: string;
  canManage: boolean;
}

const MANAGER_ROLES = ['sales_manager', 'org_owner'];

export function TeamsManager({ orgId, canManage }: TeamsManagerProps) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { data: teamsResp, isLoading } = useListTeams(orgId, {
    query: { enabled: !!orgId },
  });
  const teams: Team[] = teamsResp?.status === 200 ? teamsResp.data : [];

  const invalidateTeams = () =>
    queryClient.invalidateQueries({ queryKey: getListTeamsQueryKey(orgId) });

  const createTeam = useCreateTeam({ mutation: { onSuccess: invalidateTeams } });
  const updateTeam = useUpdateTeam({ mutation: { onSuccess: invalidateTeams } });
  const deleteTeam = useDeleteTeam({ mutation: { onSuccess: invalidateTeams } });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Team | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Team | null>(null);
  const [rosterTeamId, setRosterTeamId] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setFormOpen(true);
  };

  const openEdit = (team: Team) => {
    setEditing(team);
    setName(team.name);
    setDescription(team.description ?? '');
    setFormOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updateTeam.mutateAsync({ orgId, teamId: editing.id, data: { name, description } });
        showSnackbar('Team updated');
      } else {
        await createTeam.mutateAsync({ orgId, data: { name, description } });
        showSnackbar('Team created');
      }
      setFormOpen(false);
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  const handleDelete = async (team: Team) => {
    try {
      await deleteTeam.mutateAsync({ orgId, teamId: team.id });
      showSnackbar('Team deleted');
      setConfirmDelete(null);
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
      setConfirmDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {canManage && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<GroupAddIcon />} onClick={openCreate}>
            New Team
          </Button>
        </Box>
      )}
      {teams.length === 0 ? (
        <Typography
          sx={{
            color: "text.secondary",
            mt: 2
          }}>
          No teams yet.{canManage ? ' Create one to start grouping reps.' : ''}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Managers</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell>{team.name}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{team.description}</TableCell>
                  <TableCell align="right">{team.member_count}</TableCell>
                  <TableCell align="right">{team.manager_count}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Manage roster">
                      <IconButton
                        size="small"
                        onClick={() => setRosterTeamId(team.id)}
                        aria-label="Manage roster"
                      >
                        <PeopleIcon />
                      </IconButton>
                    </Tooltip>
                    {canManage && (
                      <>
                        <Tooltip title="Edit team">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(team)}
                            aria-label="Edit team"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete team">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setConfirmDelete(team)}
                            aria-label="Delete team"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Create / edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Team' : 'New Team'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Team Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={name.trim().length < 2 || createTeam.isPending || updateTeam.isPending}
          >
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {rosterTeamId && (
        <TeamRosterDialog
          orgId={orgId}
          teamId={rosterTeamId}
          canManage={canManage}
          onClose={() => setRosterTeamId(null)}
        />
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Team"
        message={`Delete "${confirmDelete?.name}"? Members keep their accounts; only this team and its assignments are removed.`}
        confirmLabel="Delete"
        severity="error"
        loading={deleteTeam.isPending}
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}

interface TeamRosterDialogProps {
  orgId: string;
  teamId: string;
  canManage: boolean;
  onClose: () => void;
}

function TeamRosterDialog({ orgId, teamId, canManage, onClose }: TeamRosterDialogProps) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const { data: teamResp, isLoading } = useGetTeam(orgId, teamId);
  const team = teamResp?.status === 200 ? teamResp.data : undefined;

  const { data: membersResp } = useListMembers(orgId);
  const orgMembers: Member[] = membersResp?.status === 200 ? membersResp.data : [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetTeamQueryKey(orgId, teamId) });
    queryClient.invalidateQueries({ queryKey: getListTeamsQueryKey(orgId) });
  };

  const addMember = useAddTeamMember({ mutation: { onSuccess: invalidate } });
  const removeMember = useRemoveTeamMember({ mutation: { onSuccess: invalidate } });
  const assignManager = useAssignTeamManager({ mutation: { onSuccess: invalidate } });
  const unassignManager = useUnassignTeamManager({ mutation: { onSuccess: invalidate } });

  const [memberToAdd, setMemberToAdd] = useState<Member | null>(null);
  const [managerToAdd, setManagerToAdd] = useState<Member | null>(null);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      showSnackbar(ok);
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  const memberUids = new Set((team?.members ?? []).map((m) => m.uid));
  const managerUids = new Set((team?.managers ?? []).map((m) => m.uid));

  const memberCandidates = orgMembers.filter((m) => !memberUids.has(m.uid));
  const managerCandidates = orgMembers.filter(
    (m) => MANAGER_ROLES.includes(m.role) && !managerUids.has(m.uid),
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{team ? team.name : 'Team'} — Roster</DialogTitle>
      <DialogContent dividers>
        {isLoading || !team ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <RosterSection
              title="Managers"
              roster={team.managers ?? []}
              canManage={canManage}
              onRemove={(uid) =>
                run(
                  () => unassignManager.mutateAsync({ orgId, teamId, uid }),
                  'Manager unassigned',
                )
              }
              picker={
                <Autocomplete
                  size="small"
                  options={managerCandidates}
                  value={managerToAdd}
                  onChange={(_, v) => setManagerToAdd(v)}
                  getOptionLabel={(m) => `${m.display_name} (${m.email})`}
                  isOptionEqualToValue={(a, b) => a.uid === b.uid}
                  noOptionsText="No eligible Sales Managers — promote a member first"
                  renderInput={(params) => (
                    <TextField {...params} label="Add manager" placeholder="Sales Managers" />
                  )}
                />
              }
              onAdd={
                managerToAdd
                  ? () =>
                      run(async () => {
                        await assignManager.mutateAsync({
                          orgId,
                          teamId,
                          data: { uid: managerToAdd.uid },
                        });
                        setManagerToAdd(null);
                      }, 'Manager assigned')
                  : undefined
              }
            />
            <Divider />
            <RosterSection
              title="Members"
              roster={team.members ?? []}
              canManage={canManage}
              onRemove={(uid) =>
                run(() => removeMember.mutateAsync({ orgId, teamId, uid }), 'Member removed')
              }
              picker={
                <Autocomplete
                  size="small"
                  options={memberCandidates}
                  value={memberToAdd}
                  onChange={(_, v) => setMemberToAdd(v)}
                  getOptionLabel={(m) => `${m.display_name} (${m.email})`}
                  isOptionEqualToValue={(a, b) => a.uid === b.uid}
                  renderInput={(params) => (
                    <TextField {...params} label="Add member" placeholder="Org members" />
                  )}
                />
              }
              onAdd={
                memberToAdd
                  ? () =>
                      run(async () => {
                        await addMember.mutateAsync({
                          orgId,
                          teamId,
                          data: { uid: memberToAdd.uid },
                        });
                        setMemberToAdd(null);
                      }, 'Member added')
                  : undefined
              }
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

interface RosterSectionProps {
  title: string;
  roster: TeamMemberRef[];
  canManage: boolean;
  onRemove: (uid: string) => void;
  picker: React.ReactNode;
  onAdd?: () => void;
}

function RosterSection({ title, roster, canManage, onRemove, picker, onAdd }: RosterSectionProps) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title} <Chip label={roster.length} size="small" sx={{ ml: 0.5 }} />
      </Typography>
      {roster.length === 0 ? (
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          None yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {roster.map((m) => (
            <ListItem
              key={m.uid}
              disableGutters
              secondaryAction={
                canManage ? (
                  <IconButton
                    edge="end"
                    size="small"
                    color="error"
                    onClick={() => onRemove(m.uid)}
                    aria-label={`Remove ${m.display_name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : undefined
              }
            >
              <ListItemText primary={m.display_name} secondary={m.email} />
            </ListItem>
          ))}
        </List>
      )}
      {canManage && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            mt: 1
          }}>
          <Box sx={{ flexGrow: 1 }}>{picker}</Box>
          <Button variant="outlined" size="small" onClick={onAdd} disabled={!onAdd}>
            Add
          </Button>
        </Stack>
      )}
    </Box>
  );
}
